import {
  ContainerDto,
  ContainerJourneyResponseDto,
  ContainerJourneysApi,
  NonNumericOrdersApi,
  OrderResponseDto,
  OrdersApi,
} from '@planning/app/api'
import { createApiClient } from '@planning/app/http-client'
import { axiosUnwrapper } from '@planning/app/utils'
import { DtoPaginatedList, PaginatedStore } from '@planning/stores/PaginatedStore'
import { makeObservable } from 'mobx'

export type NnrOrderContainerAssignment = {
  container: ContainerDto
  containerJourney?: ContainerJourneyResponseDto
  inboundOrder?: OrderResponseDto
  outboundOrder?: OrderResponseDto
}

export class NnrOrderContainerAssignmentPaginationStore extends PaginatedStore<NnrOrderContainerAssignment> {
  constructor(nnrOrderId: number) {
    super(async (_, __, sortingModel, filter, showCompleted) => {
      const containersPage = await axiosUnwrapper(
        createApiClient(NonNumericOrdersApi).getSuitableContainers(
          nnrOrderId,
          1,
          25,
          nnrOrderId,
          filter,
          showCompleted,
          sortingModel.orderBy,
          sortingModel.isDescending,
        ),
      )

      const containers = containersPage.payload
      const containerIds = containers
        .map(container => container.id)
        .filter((containerId): containerId is number => !!containerId)

      if (!containerIds.length) {
        return mapContainersToAssignments(containersPage, [], [])
      }

      const containerJourneys = await axiosUnwrapper(
        createApiClient(ContainerJourneysApi).getByContainerIds({
          containerIds,
          includeCompleted: showCompleted,
          includeRestow: true,
        }),
      )

      const orderIds = getOrderIds(containerJourneys)
      const orders = orderIds.length
        ? await axiosUnwrapper(createApiClient(OrdersApi).getByIds({ ids: orderIds }))
        : []

      return mapContainersToAssignments(containersPage, containerJourneys, orders)
    })
    makeObservable(this, {})
  }
}

const getOrderIds = (containerJourneys: ContainerJourneyResponseDto[]) => {
  return Array.from(
    new Set(
      containerJourneys.flatMap(containerJourney =>
        [containerJourney.inboundOrderId, containerJourney.outboundOrderId].filter(
          (orderId): orderId is number => !!orderId,
        ),
      ),
    ),
  )
}

const mapContainersToAssignments = (
  containersPage: DtoPaginatedList<ContainerDto>,
  containerJourneys: ContainerJourneyResponseDto[],
  orders: OrderResponseDto[],
): DtoPaginatedList<NnrOrderContainerAssignment> => {
  const containerJourneyByContainerId = new Map(
    containerJourneys.map(containerJourney => [containerJourney.containerId, containerJourney]),
  )
  const orderById = new Map(orders.map(order => [order.id, order]))

  return {
    ...containersPage,
    payload: containersPage.payload.map(container => {
      const containerJourney = container.id ? containerJourneyByContainerId.get(container.id) : undefined

      return {
        container,
        containerJourney,
        inboundOrder: containerJourney ? orderById.get(containerJourney.inboundOrderId) : undefined,
        outboundOrder: containerJourney?.outboundOrderId
          ? orderById.get(containerJourney.outboundOrderId)
          : undefined,
      }
    }),
  }
}
